// Pure radar-chart geometry for the player Attributes tab.
// Ported 1:1 from the design handoff — returns plain data only, no DOM/SVG.
//
// axes: [{ key, value, derived?, active? }]
// size: square viewport edge in px
// o:    { pad, labelGap, numGap }
//
// Values are clamped to 0-100 for GEOMETRY only; `nums[].value` keeps the
// raw value for display.

const DEFAULTS = { pad: 40, labelGap: 16, numGap: 12 };

export function radar(axes, size, o = {}) {
  const { pad, labelGap, numGap } = { ...DEFAULTS, ...o };
  const R = size / 2 - pad;
  const c = size / 2;
  const n = axes.length;

  // Vertex i sits at angle -90 + i*360/n degrees (first axis at top),
  // radius r from center; coordinates rounded to 1 decimal.
  const pt = (r, i) => {
    const a = ((-90 + (i * 360) / n) * Math.PI) / 180;
    return [
      +(c + r * Math.cos(a)).toFixed(1),
      +(c + r * Math.sin(a)).toFixed(1),
    ];
  };

  const clamp = (v) => {
    const num = Number(v);
    return Math.max(0, Math.min(100, Number.isFinite(num) ? num : 0));
  };

  const rings = [0.25, 0.5, 0.75, 1].map((f) => ({
    points: axes.map((_, i) => pt(R * f, i).join(",")).join(" "),
  }));

  const spokes = axes.map((_, i) => {
    const q = pt(R, i);
    return { x2: q[0], y2: q[1] };
  });

  const poly = axes
    .map((a, i) => pt((R * clamp(a.value)) / 100, i).join(","))
    .join(" ");

  const verts = [];
  const nums = [];
  const labels = [];

  axes.forEach((a, i) => {
    const rv = (R * clamp(a.value)) / 100;
    const q = pt(rv, i);
    verts.push({ x: q[0], y: q[1], derived: !!a.derived, active: !!a.active });

    // Number placement: outward of the vertex when it still fits inside the
    // rim (+4px tolerance) or when the vertex is close to the center
    // (< 42% of R); otherwise inward.
    const outward = rv + numGap <= R + 4 || rv < R * 0.42;
    const nq = pt(outward ? rv + numGap : rv - numGap, i);
    nums.push({ x: nq[0], y: nq[1], value: a.value });

    // Label anchor just outside the rim; alignment buckets from the axis
    // angle's cos/sin.
    const lq = pt(R + labelGap * 0.55, i);
    const ang = ((-90 + (i * 360) / n) * Math.PI) / 180;
    const cos = Math.cos(ang);
    const sin = Math.sin(ang);
    const hx = cos > 0.25 ? "l" : cos < -0.25 ? "r" : "c";
    const tx = hx === "l" ? "0%" : hx === "r" ? "-100%" : "-50%";
    const ty = sin < -0.3 ? "-100%" : sin > 0.3 ? "0%" : "-50%";
    const maxWidth = Math.round(hx === "c" ? size * 0.44 : size * 0.3);
    const align = hx === "l" ? "left" : hx === "r" ? "right" : "center";
    labels.push({ key: a.key, x: lq[0], y: lq[1], tx, ty, maxWidth, align });
  });

  return { size, c, rings, spokes, poly, verts, nums, labels };
}
